import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { brandsQuery, categoriesQuery, categoryLabel } from '../lib/queries';
import type { PricingRule, PricingSettings } from '../lib/types';
import { suppliersQuery } from './suppliers/list';

const SCOPES = ['global', 'market', 'category', 'supplier', 'brand', 'product'] as const;
const BASES = ['cost', 'supplier_retail'] as const;
const ROUNDING = ['none', 'x.99', 'x.95', 'x.90', 'integer'] as const;

/**
 * Правила наценки (§9.3): цена = (себестоимость + расходы) × (1 + наценка) → комиссия платёжки →
 * защита минимальной маржи → НДС рынка → округление. Действует самое конкретное правило.
 * Правило не редактируется: чтобы изменить — выключить и создать новое (история остаётся).
 */
export function PricingRulesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const rules = useQuery({
    queryKey: ['pricing', 'rules'],
    queryFn: () => api<{ items: PricingRule[] }>('/pricing/rules').then((r) => r.items),
  });
  const categories = useQuery(categoriesQuery);
  const brands = useQuery(brandsQuery);
  const suppliers = useQuery(suppliersQuery);

  const scopeTarget = (rule: PricingRule): string | null => {
    if (!rule.scopeId) return null;
    if (rule.scope === 'category') {
      const all = categories.data ?? [];
      const category = all.find((c) => c.id === rule.scopeId);
      return category ? categoryLabel(category, all, 'ru') : rule.scopeId;
    }
    if (rule.scope === 'brand') {
      return brands.data?.find((b) => b.id === rule.scopeId)?.name ?? rule.scopeId;
    }
    if (rule.scope === 'supplier') {
      return (
        suppliers.data?.items.find((s) => s.supplier.id === rule.scopeId)?.supplier.name ??
        rule.scopeId
      );
    }
    return rule.scopeId;
  };

  const toggle = async (rule: PricingRule) => {
    setError(undefined);
    try {
      await api(`/pricing/rules/${rule.id}/${rule.isActive ? 'disable' : 'enable'}`, {
        method: 'POST',
      });
      await queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const recalculate = async () => {
    setError(undefined);
    try {
      await api('/pricing/recalculate', { method: 'POST' });
      setNotice(t('pricing.recalcQueued'));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title={t('pricing.title')}
        description={t('pricing.hint')}
        actions={
          <>
            <Button variant="outline" onClick={() => void recalculate()}>
              {t('pricing.recalc')}
            </Button>
            <Button
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('pricing.new')}
            </Button>
          </>
        }
      />
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      {notice ? <Alert>{notice}</Alert> : null}
      {rules.error ? <Alert variant="destructive">{errorMessage(rules.error)}</Alert> : null}

      <SettingsCard />

      <h2 className="mb-2 mt-6 font-semibold">{t('pricing.rules')}</h2>
      {rules.data?.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('pricing.noRules')}</p>
      ) : null}
      <div className="grid gap-2">
        {(rules.data ?? []).map((r) => (
          <div
            key={r.id}
            className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm"
          >
            <span className="font-medium">{r.name}</span>
            <Badge variant="outline">{t(`pricing.scopes.${r.scope}`)}</Badge>
            {scopeTarget(r) ? (
              <span className="text-muted-foreground">{scopeTarget(r)}</span>
            ) : null}
            {r.basis === 'supplier_retail' ? (
              <span>{t('pricing.bases.supplier_retail')}</span>
            ) : (
              <span>+{Number(r.markupPct)}%</span>
            )}
            {r.markupFixedMinor > 0 ? (
              <span>+{(r.markupFixedMinor / 100).toFixed(2)} €</span>
            ) : null}
            {r.minMarginPct !== null ? (
              <span>
                {t('pricing.minMarginShort')} {Number(r.minMarginPct)}%
              </span>
            ) : null}
            <span className="text-muted-foreground">{t(`pricing.roundings.${r.rounding}`)}</span>
            {r.isActive ? null : <Badge variant="secondary">{t('common.inactive')}</Badge>}
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => void toggle(r)}>
              {r.isActive ? t('pricing.disable') : t('pricing.enable')}
            </Button>
          </div>
        ))}
      </div>
      {creating ? (
        <RuleDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}

/** Общие настройки влияют на все цены: сохранение просит пароль и код (reauth) и пересчитывает цены. */
function SettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['pricing', 'settings'],
    queryFn: () => api<PricingSettings>('/pricing/settings'),
  });
  const [form, setForm] = useState<Record<keyof PricingSettings, string>>();
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data && !form) {
      setForm({
        paymentFeePct: String(settings.data.paymentFeePct),
        operatingCostMinor: (settings.data.operatingCostMinor / 100).toFixed(2),
        handlingCostMinor: (settings.data.handlingCostMinor / 100).toFixed(2),
        changeThresholdPct: String(settings.data.changeThresholdPct),
        defaultMinMarginPct: String(settings.data.defaultMinMarginPct),
      });
    }
  }, [settings.data, form]);

  if (!form) return null;
  const set = (key: keyof PricingSettings, value: string) => {
    setForm({ ...form, [key]: value });
    setSaved(false);
  };
  const save = async () => {
    setError(undefined);
    try {
      await api('/pricing/settings', {
        method: 'PUT',
        body: {
          paymentFeePct: Number(form.paymentFeePct),
          operatingCostMinor: Math.round(Number(form.operatingCostMinor) * 100),
          handlingCostMinor: Math.round(Number(form.handlingCostMinor) * 100),
          changeThresholdPct: Number(form.changeThresholdPct),
          defaultMinMarginPct: Number(form.defaultMinMarginPct),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['pricing', 'settings'] });
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const fields: [keyof PricingSettings, string][] = [
    ['paymentFeePct', t('pricing.paymentFee')],
    ['operatingCostMinor', t('pricing.operatingCost')],
    ['handlingCostMinor', t('pricing.handlingCost')],
    ['defaultMinMarginPct', t('pricing.defaultMinMargin')],
    ['changeThresholdPct', t('pricing.changeThreshold')],
  ];
  return (
    <Card className="p-4">
      <h2 className="mb-1 font-semibold">{t('pricing.settings')}</h2>
      <p className="mb-3 text-sm text-muted-foreground">{t('pricing.settingsHint')}</p>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {fields.map(([key, label]) => (
          <Field key={key} label={label}>
            {(id) => (
              <Input
                id={id}
                type="number"
                step="0.01"
                min={0}
                value={form[key]}
                onChange={(e) => {
                  set(key, e.target.value);
                }}
              />
            )}
          </Field>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={() => void save()}>{t('common.save')}</Button>
        {saved ? <span className="text-sm text-muted-foreground">{t('pricing.saved')}</span> : null}
      </div>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
    </Card>
  );
}

function RuleDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const categories = useQuery(categoriesQuery);
  const brands = useQuery(brandsQuery);
  const suppliers = useQuery(suppliersQuery);
  const [name, setName] = useState('');
  const [basis, setBasis] = useState<(typeof BASES)[number]>('cost');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('global');
  const [scopeId, setScopeId] = useState('');
  const [markupPct, setMarkupPct] = useState('30');
  const [fixed, setFixed] = useState('');
  const [minMargin, setMinMargin] = useState('');
  const [rounding, setRounding] = useState<(typeof ROUNDING)[number]>('x.99');
  const [priority, setPriority] = useState('0');
  const [error, setError] = useState<string>();

  const save = async () => {
    setError(undefined);
    try {
      await api('/pricing/rules', {
        method: 'POST',
        body: {
          name,
          scope,
          scopeId: scope === 'global' ? null : scopeId.trim(),
          basis,
          markupPct: basis === 'cost' ? Number(markupPct) : 0,
          markupFixedMinor: basis === 'cost' && fixed ? Math.round(Number(fixed) * 100) : 0,
          ...(minMargin ? { minMarginPct: Number(minMargin) } : {}),
          rounding,
          priority: Number(priority) || 0,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['pricing', 'rules'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const allCategories = categories.data ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('pricing.new')}
      className="max-w-xl"
      footer={
        <Button
          disabled={name.trim().length < 2 || (scope !== 'global' && !scopeId.trim())}
          onClick={() => void save()}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        <Field label={t('pricing.name')}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex gap-2">
          <Field label={t('pricing.scope')}>
            {(id) => (
              <Select
                id={id}
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as (typeof SCOPES)[number]);
                  setScopeId('');
                }}
              >
                {SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`pricing.scopes.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {scope === 'category' ? (
            <Field label={t('pricing.scopes.category')} className="flex-1">
              {(id) => (
                <Select
                  id={id}
                  value={scopeId}
                  onChange={(e) => {
                    setScopeId(e.target.value);
                  }}
                >
                  <option value="">—</option>
                  {allCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {categoryLabel(c, allCategories, 'ru')}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          {scope === 'brand' ? (
            <Field label={t('pricing.scopes.brand')} className="flex-1">
              {(id) => (
                <Select
                  id={id}
                  value={scopeId}
                  onChange={(e) => {
                    setScopeId(e.target.value);
                  }}
                >
                  <option value="">—</option>
                  {(brands.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          {scope === 'supplier' ? (
            <Field label={t('pricing.scopes.supplier')} className="flex-1">
              {(id) => (
                <Select
                  id={id}
                  value={scopeId}
                  onChange={(e) => {
                    setScopeId(e.target.value);
                  }}
                >
                  <option value="">—</option>
                  {(suppliers.data?.items ?? []).map(({ supplier: s }) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
          {scope === 'market' || scope === 'product' ? (
            <Field label={t('pricing.scopeId')} hint={t('pricing.scopeIdHint')} className="flex-1">
              {(id) => (
                <Input
                  id={id}
                  value={scopeId}
                  onChange={(e) => {
                    setScopeId(e.target.value);
                  }}
                  className="font-mono"
                />
              )}
            </Field>
          ) : null}
        </div>
        <Field label={t('pricing.basis')} hint={t(`pricing.basesHint.${basis}`)}>
          {(id) => (
            <Select
              id={id}
              value={basis}
              onChange={(e) => {
                setBasis(e.target.value as (typeof BASES)[number]);
              }}
            >
              {BASES.map((b) => (
                <option key={b} value={b}>
                  {t(`pricing.bases.${b}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Field label={t('pricing.markup')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                step="0.1"
                disabled={basis !== 'cost'}
                value={markupPct}
                onChange={(e) => {
                  setMarkupPct(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('pricing.fixed')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                step="0.01"
                placeholder="0"
                disabled={basis !== 'cost'}
                value={fixed}
                onChange={(e) => {
                  setFixed(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('pricing.minMargin')} hint={t('pricing.minMarginHint')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                step="0.1"
                placeholder="—"
                value={minMargin}
                onChange={(e) => {
                  setMinMargin(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('pricing.priority')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min={0}
                value={priority}
                onChange={(e) => {
                  setPriority(e.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <Field label={t('pricing.rounding')}>
          {(id) => (
            <Select
              id={id}
              value={rounding}
              onChange={(e) => {
                setRounding(e.target.value as (typeof ROUNDING)[number]);
              }}
            >
              {ROUNDING.map((r) => (
                <option key={r} value={r}>
                  {t(`pricing.roundings.${r}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
